(() => {
  const VERSION = '2026-08-13.2';
  if (window.PBLogoV3Fixes?.version === VERSION) return;

  const ROOT_SELECTOR = '[id^="PBPhotoBoxCustomizer-"], [id^="PBUSBBoxCustomizer-"], [id^="PBStandaloneUSBCustomizer-"]';
  const MODAL_SELECTOR = '.pb-photo-customizer__modal, .pb-usb-customizer__modal, .pb-standalone-usb-customizer__modal, [data-pb-customizer-modal]';
  const HELP_HTML = '<strong>Want to preview your logo?</strong><br>For the clearest preview, upload an SVG with black or dark artwork on a white or transparent background. Black/dark areas become your selected foil or engraving finish; white/transparent areas are treated as background. If your SVG is reversed (a white logo on a black background), the black background will become the foil area. PDF and high-resolution transparent PNG files are also accepted for production, but may not display in the live preview.';

  const contexts = [];
  const contextFor = target => contexts.find(ctx => ctx.root.contains(target) || ctx.scope.contains(target)) || null;

  const defaultOwner = ctx => {
    if (ctx.root.id.startsWith('PBStandaloneUSBCustomizer-')) return 'usb';
    return 'box';
  };

  const currentOwner = ctx => ctx?.scope?.dataset?.pbPreviewOwner || defaultOwner(ctx);

  // Logo state is target-specific. A box logo and USB text can legitimately coexist in
  // the same customizer, so a logo selected for one target must never suppress text on another.
  const activeLogo = ctx => {
    if (!ctx?.scope) return false;
    const owner = currentOwner(ctx);
    if (!['box', 'usb', 'addon'].includes(owner)) return false;
    return !!ctx.scope.querySelector(`[data-pb-logo="${owner}"].is-active`);
  };

  const parseJson = (root, selector, fallback = {}) => {
    try { return JSON.parse(root?.querySelector(selector)?.textContent || JSON.stringify(fallback)); }
    catch { return fallback; }
  };

  const selectedUsbColor = ctx => {
    if (!ctx?.root || !ctx?.scope) return '#ffffff';

    let type = String(ctx.root.dataset.usbType || '');
    if (!ctx.root.id.startsWith('PBStandaloneUSBCustomizer-')) {
      const key = ctx.scope.querySelector('[data-usb-key].is-active')?.dataset?.usbKey || '';
      const products = parseJson(ctx.root, '[data-pb-usb-products-json]', {});
      type = String(products?.[key]?.type || type || 'crystal');
    }

    return type.toLowerCase().includes('wood') ? '#1D0200' : '#ffffff';
  };

  const selectedColor = (ctx, kind) => {
    if (!ctx) return null;
    if (kind === 'addon') {
      const button = ctx.scope.querySelector('[data-box-foil-name].is-active');
      return button?.dataset?.boxFoilColor || ctx.scope.dataset.pbLogoFixAddonColor || null;
    }
    if (kind === 'box') {
      const button = ctx.scope.querySelector('[data-foil].is-active');
      return button?.dataset?.foilColor || ctx.scope.dataset.pbLogoFixBoxColor || null;
    }
    if (kind === 'usb') return selectedUsbColor(ctx);
    return null;
  };

  const hexToRgb = hex => {
    const clean = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(clean)) return null;
    const full = clean.length === 3 ? clean.split('').map(x => x + x).join('') : clean;
    const number = Number.parseInt(full, 16);
    return { r: (number >> 16) & 255, g: (number >> 8) & 255, b: number & 255 };
  };

  const recolorImage = async (img, color) => {
    if (!img || !color || !/^data:image\/png/i.test(img.src || '')) return;
    if (img.dataset.pbLogoFixColor === color || img.dataset.pbLogoFixBusy === color) return;
    const rgb = hexToRgb(color);
    if (!rgb) return;

    const source = img.src;
    img.dataset.pbLogoFixBusy = color;
    try {
      const sourceImage = new Image();
      await new Promise((resolve, reject) => {
        sourceImage.onload = resolve;
        sourceImage.onerror = reject;
        sourceImage.src = source;
      });
      const width = sourceImage.naturalWidth || sourceImage.width;
      const height = sourceImage.naturalHeight || sourceImage.height;
      if (!width || !height) return;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx2d = canvas.getContext('2d', { willReadFrequently: true });
      ctx2d.drawImage(sourceImage, 0, 0, width, height);
      const pixels = ctx2d.getImageData(0, 0, width, height);
      for (let i = 0; i < pixels.data.length; i += 4) {
        if (!pixels.data[i + 3]) continue;
        pixels.data[i] = rgb.r;
        pixels.data[i + 1] = rgb.g;
        pixels.data[i + 2] = rgb.b;
      }
      ctx2d.putImageData(pixels, 0, 0);
      img.dataset.pbLogoFixColor = color;
      img.src = canvas.toDataURL('image/png');
    } catch (error) {
      console.warn('PB Logo V3 foil recolor patch unavailable', error);
    } finally {
      delete img.dataset.pbLogoFixBusy;
    }
  };

  const updateHelp = ctx => {
    ctx?.scope?.querySelectorAll('.pb-logo-v3__preview-help').forEach(el => {
      if (el.dataset.pbLogoFixHelp === '1') return;
      el.innerHTML = HELP_HTML;
      el.dataset.pbLogoFixHelp = '1';
    });
  };

  const enforceTextMode = ctx => {
    const text = ctx?.scope?.querySelector('[data-pb-preview-text]');
    if (!text) return;
    if (activeLogo(ctx)) {
      text.style.setProperty('display', 'none', 'important');
      text.setAttribute('aria-hidden', 'true');
    } else {
      text.style.removeProperty('display');
      text.removeAttribute('aria-hidden');
    }
  };

  const positionUsbPreview = ctx => {
    const preview = ctx?.scope?.querySelector('[data-pb-logo-v3-preview="usb"]');
    if (preview) preview.style.setProperty('top', '40.2%', 'important');
  };

  const recolorPreviews = ctx => {
    if (!ctx || !activeLogo(ctx)) return;
    ctx.scope.querySelectorAll('[data-pb-logo-v3-preview]').forEach(preview => {
      if (preview.hidden) return;
      const kind = preview.dataset.pbLogoV3Preview || '';
      const color = selectedColor(ctx, kind);
      const img = preview.querySelector('img');
      if (img && color) recolorImage(img, color);
    });
  };

  const sync = ctx => {
    if (!ctx) return;
    ctx.scope.classList.toggle('pb-logo-v3-mode-active', activeLogo(ctx));
    enforceTextMode(ctx);
    updateHelp(ctx);
    positionUsbPreview(ctx);
    recolorPreviews(ctx);
  };

  const schedule = ctx => {
    if (!ctx) return;
    queueMicrotask(() => sync(ctx));
    requestAnimationFrame(() => sync(ctx));
    setTimeout(() => sync(ctx), 50);
    setTimeout(() => sync(ctx), 160);
  };

  document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
    const scope = root.querySelector(MODAL_SELECTOR) || root;
    const ctx = { root, scope };
    contexts.push(ctx);
    sync(ctx);

    const observer = new MutationObserver(() => schedule(ctx));
    observer.observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'src', 'class', 'data-pb-preview-owner']
    });
  });

  document.addEventListener('click', event => {
    const ctx = contextFor(event.target);
    if (!ctx) return;

    const foil = event.target.closest('[data-foil]');
    if (foil?.dataset?.foilColor) ctx.scope.dataset.pbLogoFixBoxColor = foil.dataset.foilColor;

    const boxFoil = event.target.closest('[data-box-foil-name]');
    if (boxFoil?.dataset?.boxFoilColor) ctx.scope.dataset.pbLogoFixAddonColor = boxFoil.dataset.boxFoilColor;

    schedule(ctx);
  }, true);

  document.addEventListener('input', event => {
    const ctx = contextFor(event.target);
    if (ctx) schedule(ctx);
  }, true);

  document.addEventListener('change', event => {
    const ctx = contextFor(event.target);
    if (ctx) schedule(ctx);
  }, true);

  window.PBLogoV3Fixes = {
    version: VERSION,
    syncAll: () => contexts.forEach(sync)
  };
})();
